import { ReactNode } from 'react';
import { css } from '@emotion/react';
import { Divider } from '@frontend/divider';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { PanelContent, PanelHeader, PanelPortal } from '@frontend/panel-engine';
import { useSettingsNavigate } from '@frontend/settings-routing';
import { theme } from '@frontend/theme';
import { Heading, Text, Table, TextLink, Chip, ModalControlModalProps } from '@frontend/design-system';
import { PhoneTreePhoneNumbersTrayType } from '../shared-types';

export const PhoneTreeDetailPanel = ({
  onClose,
  modalData,
}: {
  onClose: ModalControlModalProps['onClose'];
  modalData: PhoneTreePhoneNumbersTrayType;
}) => {
  const { navigate } = useSettingsNavigate();
  const { t } = useTranslation('analytics-phone');

  return (
    <PanelPortal id={`phone-tree-analytics`} onClose={onClose} width='medium'>
      <PanelHeader css={styles.panelHeader}>
        <PanelHeader.Close />
      </PanelHeader>
      <Divider margin={0} />
      <div css={styles.panelSubHeader}>
        <Heading level={2}>{t('Phone Numbers')}</Heading>
        <PanelSubHeaderDetails>
          <Icon name='phone-tree' size={16} />
          <Text size='medium'>{t('Phone Tree: {{ name }}', { name: modalData?.phoneTreeName })}</Text>
        </PanelSubHeaderDetails>
        {!!modalData.callRouteName && (
          <PanelSubHeaderDetails>
            <Icon name='call-route' size={16} />
            <Text size='medium'>{t('Call Route')}:</Text>
            <TextLink
              size='medium'
              onClick={() => {
                navigate({ to: `/phone/call-routes/:id`, params: { id: modalData.callRouteId } });
              }}
            >
              {modalData.callRouteName}
            </TextLink>
          </PanelSubHeaderDetails>
        )}
      </div>
      <Divider margin={0} />
      <PanelContent>
        <Table
          data={modalData.phoneNumbers ?? []}
          colConfig={[
            { id: 'number', Header: 'Phone Number', accessor: (row) => row.number },
            {
              id: 'location',
              Header: 'Location',
              accessor: (row) => row.locationName,
              cellRenderer: (name) => <Chip.Location variant='primaryDark'>{name}</Chip.Location>,
            },
          ]}
        />
      </PanelContent>
    </PanelPortal>
  );
};

const PanelSubHeaderDetails = ({ children }: { children: ReactNode }) => {
  return <div css={styles.panelSubHeaderDetails}>{children}</div>;
};

const styles = {
  panelHeader: css`
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    padding-bottom: 0;
    padding: ${theme.spacing(1, 2)};
  `,
  panelSubHeader: css`
    padding: ${theme.spacing(1, 2)};
  `,
  panelSubHeaderDetails: css`
    display: flex;
    align-items: center;
    gap: ${theme.spacing(0.5)};
    margin-top: ${theme.spacing(0.5)};
    margin-bottom: ${theme.spacing(1)};
  `,
};
